import React, { useState, useEffect, useMemo } from 'react';
import { Modal } from '../Common/Modal';
import auditService from '../../services/auditService';
import { useProject } from '../../context/ProjectContext';
import { formatPerformedOnUtcToLocal, formatOffsetForDisplay } from '../../utils/dateFormat';
import { getAuditFieldDisplayName } from '../../utils/auditFieldDisplayNames';
import Spinner from '../Common/Spinner';
import './AuditDetailModal.css';

const ACTION_LABELS = { 1: 'Created', 2: 'Updated', 3: 'Deleted' };

const truncate = (value, len = 120) => {
  if (value == null || value === '') return '—';
  const s = String(value);
  return s.length > len ? s.slice(0, len) + '…' : s;
};

const cell = (v, maxLen = 80) => {
  if (v == null || v === '') return '—';
  const s = String(v);
  return s.length > maxLen ? s.slice(0, maxLen) + '…' : s;
};

/** Performed on UTC: shown as local time (UTC + offset) */
const formatPerformedOnUtc = (d, offset) => (d ? formatPerformedOnUtcToLocal(d, offset) : '—');

/**
 * Modal that shows field-level audit trail (AuditHdr + AuditDtl) for a record.
 * Used by CRF Report "View Audit Detail" and can be reused for other tables.
 * @param {boolean} isOpen
 * @param {Function} onClose
 * @param {string} tableName - e.g. 'CRFHdr', 'ScheduledVisits'
 * @param {string|number} recordId - Record ID
 * @param {string} title - Modal title (default: "Audit Detail")
 * @param {string} displayMode - 'cards' (expandable per event) or 'table' (flat table like Field Audit)
 * @param {string|number} [projectId] - Optional ProjectMst.nProjectId filter
 * @param {string|number} [siteId] - Optional SiteMst.nSiteId filter
 * @param {string} [projectCode] - Optional project code filter (used if projectId not set)
 */
const AuditDetailModal = ({
  isOpen,
  onClose,
  tableName,
  recordId,
  title = 'Audit Detail',
  displayMode = 'cards',
  onDate,
  projectId: projectIdProp,
  siteId: siteIdProp,
  projectCode: projectCodeProp,
}) => {
  const { currentProject, projectInfo } = useProject();
  const projectCodeFromContext =
    projectCodeProp ??
    (currentProject && currentProject !== 'all' ? currentProject : undefined);
  const projectIdFromContext = projectInfo?.projectId ?? projectInfo?.ProjectId;
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedIndex, setExpandedIndex] = useState(null);

  useEffect(() => {
    if (!isOpen || tableName == null || recordId == null) {
      setList([]);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    const resolvedProjectId =
      projectIdProp != null && projectIdProp !== ''
        ? projectIdProp
        : projectIdFromContext;
    auditService
      .getAuditHdrTrail(tableName, recordId, 100, {
        onDate,
        projectId: resolvedProjectId,
        siteId: siteIdProp,
        projectCode:
          resolvedProjectId != null && resolvedProjectId !== ''
            ? undefined
            : projectCodeFromContext,
      })
      .then((data) => {
        if (!cancelled) {
          setList(Array.isArray(data) ? data : []);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.response?.data?.error || err?.message || 'Failed to load audit detail');
          setList([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [isOpen, tableName, recordId, onDate, projectIdProp, siteIdProp, projectCodeFromContext, projectIdFromContext]);

  const toggleExpand = (index) => {
    setExpandedIndex((prev) => (prev === index ? null : index));
  };

  // Flatten to rows for table display (Field, Old Value, New Value, Performed on UTC, Performed By from AuditDtl.vRecordedSign)
  const flatRows = useMemo(() => {
    if (displayMode !== 'table' || !list.length) return [];
    const rows = [];
    list.forEach((hdr) => {
      const details = hdr.details || hdr.Details || [];
      const performedOn = hdr.dRecordedOnUTC || hdr.dRecordedOnUtc;
      const offset = hdr.vRecordedAtOffSet || hdr.vRecordedAtOffset;
      const headerPerformedBy = hdr.performedBy || hdr.vRecordedSign || '—';
      const tableName = hdr.vTableName ?? hdr.VTableName;
      details.forEach((dtl) => {
        const performedBy = dtl.vRecordedSign ?? dtl.VRecordedSign ?? headerPerformedBy;
        const rawFieldName = dtl.vFieldName ?? dtl.VFieldName ?? '—';
        rows.push({
          fieldName: rawFieldName,
          fieldDisplay: getAuditFieldDisplayName(rawFieldName, tableName),
          oldValue: dtl.vOldValue ?? dtl.VOldValue,
          newValue: dtl.vNewValue ?? dtl.VNewValue,
          performedOnUtc: performedOn,
          performedBy,
          offset,
        });
      });
    });
    return rows;
  }, [list, displayMode]);

  const isMailManagementAuditTable = displayMode === 'table' && (tableName || '').toLowerCase() === 'mailmanagementmst';
  const mailAuditRows = useMemo(() => {
    if (!isMailManagementAuditTable || !list.length) return [];
    return list.map((hdr, idx) => ({
      key: hdr.nAuditHdrNo ?? idx,
      performedOnUtc: hdr.dRecordedOnUTC || hdr.dRecordedOnUtc,
      performedBy: hdr.performedBy || hdr.vRecordedSign || '—',
      offset: hdr.vRecordedAtOffSet || hdr.vRecordedAtOffset || '—',
    }));
  }, [isMailManagementAuditTable, list]);

  const footer = (
    <button type="button" className="btn btn-secondary" onClick={onClose}>
      Close
    </button>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="large"
      footer={footer}
      showCloseButton={true}
    >
      <div className="audit-detail-modal-body">
        {loading && (
          <div className="audit-detail-loading">
            <Spinner />
            <span>Loading audit trail…</span>
          </div>
        )}
        {error && (
          <div className="audit-detail-error alert alert-danger">
            {error}
          </div>
        )}
        {!loading && !error && list.length === 0 && (
          <div className="audit-detail-empty text-muted">
            No audit entries found for this record.
          </div>
        )}
        {!loading && !error && isMailManagementAuditTable && mailAuditRows.length > 0 && (
          <div className="table-wrap">
            <table className="audit-table audit-detail-table">
              <thead>
                <tr>
                  <th>Performed By</th>
                  <th>Performed On UTC</th>
                  <th>Perform At Offset</th>
                </tr>
              </thead>
              <tbody>
                {mailAuditRows.map((r) => (
                  <tr key={r.key}>
                    <td title={r.performedBy}>{cell(r.performedBy, 60)}</td>
                    <td>{formatPerformedOnUtc(r.performedOnUtc, r.offset)}</td>
                    <td>{cell(r.offset, 20)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!loading && !error && !isMailManagementAuditTable && displayMode === 'table' && flatRows.length > 0 && (
          <div className="table-wrap">
            <table className="audit-table audit-detail-table">
              <thead>
                <tr>
                  <th>Field</th>
                  <th>Old Value</th>
                  <th>New Value</th>
                  <th>Performed By</th>
                  <th>Performed on UTC</th>
                  <th>Performed At Offset</th>
                </tr>
              </thead>
              <tbody>
                {flatRows.map((r, idx) => (
                  <tr key={idx}>
                    <td className="audit-detail-field" title={r.fieldName}>{cell(r.fieldDisplay ?? r.fieldName, 40)}</td>
                    <td title={r.oldValue}>{cell(r.oldValue)}</td>
                    <td title={r.newValue}>{cell(r.newValue)}</td>
                    <td title={r.performedBy}>{cell(r.performedBy, 40)}</td>
                    <td>{formatPerformedOnUtc(r.performedOnUtc, r.offset)}</td>
                    <td>{formatOffsetForDisplay(r.offset) === '—' ? '—' : formatOffsetForDisplay(r.offset)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!loading && !error && displayMode !== 'table' && list.length > 0 && (
          <div className="audit-detail-list">
            {list.map((hdr, index) => {
              const details = hdr.details || hdr.Details || [];
              const eventLabel = ACTION_LABELS[hdr.iAction] || hdr.vEvent || 'Change';
              const recordedOn = hdr.dRecordedOnUTC || hdr.dRecordedOnUtc;
              const sign = hdr.performedBy || hdr.vRecordedSign || '—';
              const isExpanded = expandedIndex === index;

              return (
                <div key={hdr.nAuditHdrNo ?? index} className="audit-detail-card">
                  <button
                    type="button"
                    className="audit-detail-card-header"
                    onClick={() => toggleExpand(index)}
                    aria-expanded={isExpanded}
                  >
                    <span className="audit-detail-event">{eventLabel}</span>
                    <span className="audit-detail-meta" title="Performed on UTC (shown as local time)">
                      {recordedOn ? formatPerformedOnUtcToLocal(recordedOn, hdr.vRecordedAtOffSet || hdr.vRecordedAtOffset) : '—'}
                    </span>
                    <span className="audit-detail-sign" title={sign}>
                      {truncate(sign, 50)}
                    </span>
                    <span className="audit-detail-toggle">
                      {details.length > 0 ? (isExpanded ? '▼' : '▶') : null}
                    </span>
                  </button>
                  {isExpanded && details.length > 0 && (
                    <div className="audit-detail-fields">
                      <table className="audit-detail-table">
                        <thead>
                          <tr>
                            <th>Field</th>
                            <th>Old Value</th>
                            <th>New Value</th>
                          </tr>
                        </thead>
                        <tbody>
                          {details.map((dtl, i) => (
                            <tr key={dtl.nAuditDtlNo ?? i}>
                              <td
                                className="audit-detail-field"
                                title={dtl.vFieldName ?? dtl.VFieldName ?? ''}
                              >
                                {getAuditFieldDisplayName(
                                  dtl.vFieldName ?? dtl.VFieldName ?? '',
                                  hdr.vTableName ?? hdr.VTableName
                                ) || '—'}
                              </td>
                              <td className="audit-detail-old" title={dtl.vOldValue ?? dtl.VOldValue}>
                                {truncate(dtl.vOldValue ?? dtl.VOldValue, 80)}
                              </td>
                              <td className="audit-detail-new" title={dtl.vNewValue ?? dtl.VNewValue}>
                                {truncate(dtl.vNewValue ?? dtl.VNewValue, 80)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        {!loading && !error && displayMode === 'table' && list.length > 0 && flatRows.length === 0 && mailAuditRows.length === 0 && (
          <div className="audit-detail-empty text-muted">
            No field changes recorded for this record.
          </div>
        )}
      </div>
    </Modal>
  );
};

export default AuditDetailModal;
